/**
 * Airport Search Service
 * Provides airport search functionality using React Query
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

/**
 * React Query hook to fetch all airports
 * @returns {Object} React Query result with airports data
 */
export function useAirports() {
  return useQuery({
    queryKey: ['airports'],
    queryFn: async () => {
      const data = await apiClient.getAirports();
      return Array.isArray(data) ? data : (data?.data || []);
    },
    staleTime: Infinity, // Airports data rarely changes
    cacheTime: Infinity, // Keep in cache forever
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Search airports by query string (client-side filtering)
 * Prioritizes ICAO code matches first, then other fields
 * @param {Array} airports - Array of airport objects
 * @param {string} query - Search query (matches against code, name, city, or country)
 * @returns {Array} - Array of matching airports (max 10 results)
 */
export function filterAirports(airports, query) {
  if (!airports || !Array.isArray(airports)) {
    return [];
  }

  if (!query || typeof query !== 'string') {
    return airports.slice(0, 10);
  }

  const normalizedQuery = query.toLowerCase().trim();

  // Handle US airport convention: KTEB -> TEB (strip leading K for 4-char codes)
  const strippedQuery = normalizedQuery.length === 4 && normalizedQuery.startsWith('k')
    ? normalizedQuery.slice(1)
    : null;

  // Separate matches into priority groups
  const exactIcaoMatches = [];
  const startsWithIcaoMatches = [];
  const containsIcaoMatches = [];
  const otherMatches = [];

  // Additional priority groups for secondary code (IATA/FAA) matches
  const exactCodeMatches = [];
  const startsWithCodeMatches = [];
  const containsCodeMatches = [];

  airports.forEach(airport => {
    const icao = airport.icao?.toLowerCase() || '';
    const code = airport.code?.toLowerCase() || ''; // IATA/FAA code

    // Priority 1: Exact ICAO match
    if (icao === normalizedQuery || (strippedQuery && icao === strippedQuery)) {
      exactIcaoMatches.push(airport);
    }
    // Priority 2: ICAO starts with query
    else if (icao.startsWith(normalizedQuery) || (strippedQuery && icao.startsWith(strippedQuery))) {
      startsWithIcaoMatches.push(airport);
    }
    // Priority 3: ICAO contains query
    else if (icao.includes(normalizedQuery)) {
      containsIcaoMatches.push(airport);
    }
    // Priority 4: Exact secondary code match
    else if (code === normalizedQuery || (strippedQuery && code === strippedQuery)) {
      exactCodeMatches.push(airport);
    }
    // Priority 5: Secondary code starts with query
    else if (code.startsWith(normalizedQuery) || (strippedQuery && code.startsWith(strippedQuery))) {
      startsWithCodeMatches.push(airport);
    }
    // Priority 6: Secondary code contains query
    else if (code.includes(normalizedQuery)) {
      containsCodeMatches.push(airport);
    }
    // Priority 7: Other fields match (name, city, country)
    else if (
      airport.name?.toLowerCase().includes(normalizedQuery) ||
      airport.city?.toLowerCase().includes(normalizedQuery) ||
      airport.country?.toLowerCase().includes(normalizedQuery)
    ) {
      otherMatches.push(airport);
    }
  });

  // Sort matches alphabetically by ICAO code for consistent ordering
  const sortByIcao = (a, b) => (a.icao || a.code || '').localeCompare(b.icao || b.code || '');
  exactIcaoMatches.sort(sortByIcao);
  startsWithIcaoMatches.sort(sortByIcao);
  containsIcaoMatches.sort(sortByIcao);
  exactCodeMatches.sort(sortByIcao);
  startsWithCodeMatches.sort(sortByIcao);
  containsCodeMatches.sort(sortByIcao);

  // Combine results in priority order (ICAO matches first, then secondary code matches)
  const results = [
    ...exactIcaoMatches,
    ...startsWithIcaoMatches,
    ...containsIcaoMatches,
    ...exactCodeMatches,
    ...startsWithCodeMatches,
    ...containsCodeMatches,
    ...otherMatches
  ];

  // Return max 10 results
  return results.slice(0, 10);
}
